/* eslint-disable react/no-unstable-nested-components */
import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
// Libs
import { useTranslation } from 'react-i18next';
import {
  useReactTable, flexRender, getCoreRowModel, getSortedRowModel,
} from '@tanstack/react-table';
import { AnimatePresence } from 'framer-motion';
// Utils
import { cn } from '@repo/zolintra-utils';
// Components
import {
  ContextMenu,
  Icon,
  MOBILE_MEDIA_QUERY,
  Skeleton,
  Tag,
  useMediaQuery,
} from '@repo/zolintra-ui';
import DeleteConfirmDialog from '../../dialogTemplates/DeleteConfirmDialog';
import ReadRemoteWorkingForm from '../../formTemplates/ReadRemoteWorkingForm';
// Permissions
import hasPermission, { PERMISSIONS } from '../../../../permissions/hasPermission';
// Hooks
import usePost, { POST_STATUS } from '../../../../hooks/domain/usePost';
import { useDialog } from '../../../../hooks/useDialog';
import useSearchParams from '../../../../hooks/useSearchParams';
import useResources from '../../../../hooks/domain/useResources';
import { useAppContext } from '../../../../store/AppContext';
import usePostsTable from '../../../../hooks/domain/usePostsTable';
// Services
import { useResourcesListQuery } from '../../../../services/resource/resourceQueries';

// Styles
import styles from './Tables.module.css';
import { PostCards } from './PostCards';

function RemoteWorkingTable({ data, asyncActions, isPostListFetching }) {
  // Hooks
  const [{ user }] = useAppContext();
  const { getSearchParams } = useSearchParams();
  const { t } = useTranslation();
  const {
    getPostStatusTagStyling,
    displayDateFromSlotId,
    hasDeletePostPermission,
  } = usePost();
  const { setDialog } = useDialog();
  const { getResourceDisplayName } = useResources();

  // States
  const {
    year = new Date().getFullYear().toString(),
  } = getSearchParams();
  const [sorting, setSorting] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

  // Permissions
  const hasAnyPostAndResourcePermission = (
    hasPermission(PERMISSIONS.GET_ANY_RESOURCE, user.prm)
    && hasPermission(PERMISSIONS.GET_ANY_POST, user.prm)
    && hasPermission(PERMISSIONS.IDENTITY_ACCESS_GET_ANY_RESOURCE, user.prm)
  );
  const hasGetPostDetailsPermission = (
    hasPermission(PERMISSIONS.GET_ANY_REMOTE_WORKING_POST, user.prm)
    || hasPermission(PERMISSIONS.GET_OWN_REMOTE_WORKING_POST, user.prm)
  );

  // API calls
  const resourcesListQuery = useResourcesListQuery({
    enabled: hasAnyPostAndResourcePermission,
  });

  // Table custom sorting functions
  const { sortingFunctions } = usePostsTable({ resourcesList: resourcesListQuery.data });

  // Table definition
  const columns = useMemo(() => [
    {
      accessorKey: 'status',
      header: t('remoteWorkingAndLeave.listSubView.table.statusLabel'),
      cell: ({ cell }) => {
        const tagProps = getPostStatusTagStyling(cell.getValue());
        return (
          <div className={styles['tag-cell']}>
            <Tag color={tagProps.color} size="small">
              {t(tagProps.key)}
            </Tag>
          </div>
        );
      },
    },
    {
      accessorKey: 'resourceId',
      sortingFn: sortingFunctions.sortResourceByName,
      header: t('remoteWorkingAndLeave.listSubView.table.resourceLabel'),
      cell: ({ cell }) => {
        const resourceId = cell.getValue();
        const resource = resourcesListQuery?.data?.find((res) => res.id === resourceId);

        return getResourceDisplayName(resource);
      },
      hidden: !hasAnyPostAndResourcePermission,
    },
    {
      accessorKey: 'includedStartSlotId',
      header: t('remoteWorkingAndLeave.listSubView.table.startLabel'),
      cell: ({ cell }) => displayDateFromSlotId({
        slotId: cell.getValue(),
        year,
        locale: 'fr',
      }),
    },
    {
      accessorKey: 'excludedEndSlotId',
      header: t('remoteWorkingAndLeave.listSubView.table.endLabel'),
      cell: ({ cell }) => displayDateFromSlotId({
        slotId: cell.getValue() - 1, // because excluded end slot
        year,
        locale: 'fr',
      }),
    },
    {
      accessorKey: 'duration',
      sortingFn: sortingFunctions.sortDuration,
      header: t('remoteWorkingAndLeave.listSubView.table.intervelLabel'),
      cell: (info) => t('remoteWorkingAndLeave.listSubView.table.intervalUnit', {
        count: info.row.original.businessDaysSlotCount / 2,
      }),
    },
    {
      accessorKey: 'creationTimestamp',
      header: t('remoteWorkingAndLeave.listSubView.table.createdlLabel'),
      cell: (info) => new Date(info.row.original.creationTimestamp * 1000).toLocaleDateString(),
    },
  ], [resourcesListQuery?.data]);

  const table = useReactTable({
    data,
    columns: columns.filter((col) => !col.hidden),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Context Menu Actions
  const getRowActions = (row) => [
    {
      label: t('remoteWorkingAndLeave.listSubView.table.acceptActionLabel'),
      onClick: () => asyncActions.acceptPost(
        { year, postId: row.original.id },
      ),
      hidden: (
        !hasPermission(PERMISSIONS.ACCEPT_POST, user.prm)
        || row?.original?.status === POST_STATUS.ACCEPTED
      ),
    },
    {
      label: t('remoteWorkingAndLeave.listSubView.table.refuseActionLabel'),
      onClick: () => asyncActions.refusePost(
        { year, postId: row.original.id },
      ),
      hidden: (
        !hasPermission(PERMISSIONS.REFUSE_POST, user.prm)
        || row?.original?.status === POST_STATUS.ACCEPTED
        || row?.original?.status === POST_STATUS.REFUSED
      ),
    },
    {
      label: t('remoteWorkingAndLeave.listSubView.table.deleteActionLabel'),
      onClick: () => setDialog({
        type: 'SHOW_DIALOG',
        payload: {
          title: t('remoteWorkingAndLeave.deleteDialog.title'),
          children: (
            <ErrorBoundary fallback={<>{t('common.wrong')}</>}>
              <DeleteConfirmDialog
                request={row.original}
                year={year}
              />
            </ErrorBoundary>
          ),
          onClose: () => setDialog({ type: 'HIDE_DIALOG' }),
        },
      }),
      style: { color: 'var(--alert-500)' },
      hidden: (
        !hasDeletePostPermission({ year, post: row?.original })
      ),
    },
  ];

  // Handlers
  const handleRowRightClick = (e, row) => {
    e.preventDefault();
    setContextMenu({ position: { x: e.clientX, y: e.clientY }, context: row });
  };

  const handleRowLeftClick = (row) => {
    if (!hasGetPostDetailsPermission) return;

    setDialog({
      type: 'SHOW_DIALOG',
      payload: {
        title: hasAnyPostAndResourcePermission ? `${t(
          'remoteWorkingAndLeave.readRemoteWorkingForm.title',
        )} (${getResourceDisplayName(
          resourcesListQuery.data?.find((res) => res.id === row.original.resourceId),
        )})` : t('remoteWorkingAndLeave.readRemoteWorkingForm.title'),
        topRightContent: (
          <Tag
            color={getPostStatusTagStyling(row.original.status).color}
            size="medium"
            className={styles['ws-nowrap']}
          >
            {t(getPostStatusTagStyling(row.original.status).key)}
          </Tag>),
        children: (
          <ErrorBoundary fallback={<>{t('common.wrong')}</>}>
            <ReadRemoteWorkingForm postData={row.original} />
          </ErrorBoundary>
        ),
        onClose: () => setDialog({ type: 'HIDE_DIALOG' }),
      },
    });
  };

  const isLoading = !data
    || (hasAnyPostAndResourcePermission && !resourcesListQuery?.data)
    || isPostListFetching;

  if (isMobile) {
    return (
      <AnimatePresence>
        {isLoading ? <PostCards isLoading /> : (
          <PostCards
            table={table}
            rows={table.getRowModel().rows}
            onOpen={hasGetPostDetailsPermission ? handleRowLeftClick : undefined}
            getActions={getRowActions}
          />
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {isLoading
        ? (
          <table className="table">
            <thead>
              <tr>
                <th aria-label="loader"><Skeleton shape="rectangle" width="100%" height="2.5rem" /></th>
                <th aria-label="loader"><Skeleton shape="rectangle" width="100%" height="2.5rem" /></th>
                <th aria-label="loader"><Skeleton shape="rectangle" width="100%" height="2.5rem" /></th>
                <th aria-label="loader"><Skeleton shape="rectangle" width="100%" height="2.5rem" /></th>
                <th aria-label="loader"><Skeleton shape="rectangle" width="100%" height="2.5rem" /></th>
                <th aria-label="loader"><Skeleton shape="rectangle" width="100%" height="2.5rem" /></th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((key, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={index}>
                  <td aria-label="loader"><Skeleton shape="rectangle" width="100%" height="3.75rem" /></td>
                  <td aria-label="loader"><Skeleton shape="rectangle" width="100%" height="3.75rem" /></td>
                  <td aria-label="loader"><Skeleton shape="rectangle" width="100%" height="3.75rem" /></td>
                  <td aria-label="loader"><Skeleton shape="rectangle" width="100%" height="3.75rem" /></td>
                  <td aria-label="loader"><Skeleton shape="rectangle" width="100%" height="3.75rem" /></td>
                  <td aria-label="loader"><Skeleton shape="rectangle" width="100%" height="3.75rem" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )
        : (
          <>
            <table className="table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className={cn([header.id === 'actions' && styles['actions-header']])}
                      >
                        {header.isPlaceholder ? null : (
                          <button
                            type="button"
                            className={header.column.getIsSorted() ? 'sorted' : ''}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                            {{
                              asc: <Icon icon="arrowDown" style={{ transform: 'rotate(180deg)' }} />,
                              desc: <Icon icon="arrowDown" />,
                            }[header.column.getIsSorted()] ?? null}
                          </button>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table
                  .getRowModel()
                  .rows
                  .map((row) => (
                    <tr
                      key={row.id}
                      className={cn([
                        styles.row,
                        hasGetPostDetailsPermission ? styles.clickable : '',
                      ])}
                      onContextMenu={(e) => handleRowRightClick(e, row)}
                      onClick={() => handleRowLeftClick(row)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
            <ContextMenu
              position={contextMenu?.position}
              isDisplayed={!!contextMenu}
              onClose={() => setContextMenu(null)}
              actions={contextMenu ? getRowActions(contextMenu.context) : []}
            />
          </>
        )}
    </AnimatePresence>
  );
}

RemoteWorkingTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  asyncActions: PropTypes.shape({
    acceptPost: PropTypes.func,
    refusePost: PropTypes.func,
  }),
  isPostListFetching: PropTypes.bool,
};

RemoteWorkingTable.defaultProps = {
  data: [],
  asyncActions: {
    acceptPost: null,
    refusePost: null,
  },
  isPostListFetching: false,
};

export default RemoteWorkingTable;
